import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { generateBatch, generateOne } from '../../../../services/creativeService';
import { getRequest, patchRequest } from '../../../../services/api/apiService';

// ── Studio créa INTÉGRÉ au tunnel « pub depuis Shopify » ─────────
// Tout se passe SUR LE MÊME ÉCRAN que la pub : choix de l'usage (UGC,
// cinématographique, photo/animation produit, promo), modèle recommandé par
// le moteur serveur (/api/studio/recommendations — forces documentées des
// modèles, pas de tests payants), prompt pré-rempli depuis le produit, puis
// par média généré : Attacher à la pub · Recréer · Améliorer (instruction)
// · Image → vidéo Veo (édition Google, frame + instruction).
const USE_CASE_UI = [
  { key: 'ugc',               icon: '🤳', hint: 'Témoignage selfie, authentique' },
  { key: 'cinematic',         icon: '🎬', hint: 'Rendu cinéma, lumière dramatique' },
  { key: 'product-shot',      icon: '📸', hint: 'Photo studio du produit' },
  { key: 'product-animation', icon: '🌀', hint: 'La photo du produit prend vie' },
  { key: 'promo-text',        icon: '🏷️', hint: 'Visuel promo avec prix/texte' },
];

// Prompt de départ par usage, construit depuis le produit (modifiable ensuite).
const promptFor = (useCase, product) => {
  const title = product?.title || 'le produit';
  const price = product?.price ? ` (${product.price})` : '';
  switch (useCase) {
    case 'ugc':
      return `Vidéo UGC style selfie : une personne enthousiaste tient ${title} face caméra, lumière naturelle, ton authentique, format vertical, elle explique pourquoi elle adore le produit.`;
    case 'cinematic':
      return `Publicité cinématographique premium pour ${title} : travelling lent, lumière dramatique, gros plan produit, ambiance haut de gamme, colorimétrie cinéma.`;
    case 'product-shot':
      return `Photo produit studio de ${title} : fond épuré, éclairage professionnel, reflets doux, très haute netteté, style e-commerce premium.`;
    case 'product-animation':
      return `Anime cette photo de ${title} : léger mouvement de caméra orbital, particules lumineuses subtiles, produit mis en valeur.`;
    case 'promo-text':
      return `Visuel promotionnel pour ${title}${price} : texte "OFFRE LIMITÉE" bien lisible, prix affiché, composition publicitaire accrocheuse, couleurs vives.`;
    default:
      return `Créa publicitaire pour ${title}.`;
  }
};

const InlineCreativeStudio = ({ product, draftId, onAttached }) => {
  const [recos, setRecos] = useState(null);          // useCase → {label, best, ranked}
  const [useCase, setUseCase] = useState('cinematic');
  const [model, setModel] = useState(null);          // modèle choisi (défaut: best)
  const [prompt, setPrompt] = useState(promptFor('cinematic', product));
  const [aspect, setAspect] = useState('9:16');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);        // [{url, type}]
  const [busyUrl, setBusyUrl] = useState(null);      // média en cours d'action
  const [editTarget, setEditTarget] = useState(null); // {url, mode:'improve'|'veo'}
  const [editPrompt, setEditPrompt] = useState('');

  useEffect(() => {
    getRequest('/api/studio/recommendations')
      .then((r) => setRecos(r.useCases || null))
      .catch(() => {});
  }, []);

  const reco = recos?.[useCase];
  const activeModel = model || reco?.best || null;
  const isVideoModel = activeModel && activeModel.capability !== 'text2image';

  const pickUseCase = (key) => {
    setUseCase(key);
    setModel(null); // retour au recommandé
    setPrompt(promptFor(key, product));
    const best = recos?.[key]?.best;
    setAspect(best && best.capability !== 'text2image' ? '9:16' : '1:1');
  };

  const generate = useCallback(async (overridePrompt) => {
    if (!activeModel) return toast.error('Aucun modèle disponible — vérifiez les clés API des providers.');
    setLoading(true);
    try {
      const type = activeModel.capability === 'text2image' ? 'image' : 'video';
      const res = await generateBatch({
        prompt: overridePrompt || prompt,
        count: type === 'image' ? 2 : 1,
        aspectRatio: aspect,
        type,
        model: activeModel.id,
      });
      const ok = (res?.results || []).filter((r) => r.ok && r.url).map((r) => ({ url: r.url, type: r.type || type }));
      if (ok.length === 0) toast.warn('Génération sans résultat — réessayez ou changez de modèle.');
      setResults((prev) => [...ok, ...prev]);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Échec de la génération.');
    } finally {
      setLoading(false);
    }
  }, [activeModel, prompt, aspect]);

  const attach = async (media) => {
    setBusyUrl(media.url);
    try {
      const res = await patchRequest(`/api/shopify-ad-drafts/${draftId}`, {
        creative: { type: media.type, url: media.url, prompt },
      });
      toast.success('Créa attachée à votre pub ✅');
      onAttached?.(res.draft);
    } catch {
      toast.error("Impossible d'attacher la créa.");
    } finally {
      setBusyUrl(null);
    }
  };

  // Améliorer : instruction ajoutée au prompt → nouvelle génération.
  // Veo (Google) : la frame image + instruction → vidéo éditée (edit-video).
  const submitEdit = async () => {
    if (!editTarget || !editPrompt.trim()) return;
    const { url, mode } = editTarget;
    setEditTarget(null);
    if (mode === 'improve') {
      const newPrompt = `${prompt} — ${editPrompt.trim()}`;
      setPrompt(newPrompt);
      await generate(newPrompt);
    } else {
      setBusyUrl(url);
      setLoading(true);
      try {
        const res = await generateOne({ type: 'edit-video', prompt: editPrompt.trim(), imageUrl: url, aspectRatio: '9:16' });
        if (res?.url) {
          setResults((prev) => [{ url: res.url, type: 'video' }, ...prev]);
          toast.success('Vidéo générée depuis votre image (Google Veo) 🎞️');
        }
      } catch (e) {
        toast.error(e?.response?.data?.message || "Échec de l'édition Veo.");
      } finally {
        setBusyUrl(null);
        setLoading(false);
      }
    }
    setEditPrompt('');
  };

  return (
    <div className="p-4 mb-4 bg-white border border-fuchsia-200 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
        <p className="text-sm font-bold text-[#221C51]">🎬 Créa IA pour cette pub — sans quitter l'écran</p>
        {activeModel && (
          <span className="text-[10px] font-semibold text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 rounded-full px-2.5 py-1">
            Recommandé : {activeModel.label} · ≈ {activeModel.credits} crédit{activeModel.credits > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Usages */}
      <div className="flex gap-2 flex-wrap mb-3">
        {USE_CASE_UI.map((u) => (
          <button
            key={u.key}
            onClick={() => pickUseCase(u.key)}
            title={u.hint}
            className={`text-xs font-semibold rounded-full px-3 py-1.5 border transition ${
              useCase === u.key ? 'bg-[#221C51] text-white border-[#221C51]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#221C51]/40'
            }`}
          >
            {u.icon} {recos?.[u.key]?.label || u.key}
          </button>
        ))}
      </div>

      {/* Alternatives de modèle pour l'usage choisi */}
      {reco?.ranked?.length > 1 && (
        <div className="flex gap-1.5 flex-wrap mb-3">
          {reco.ranked.filter((m) => m.available).slice(0, 4).map((m) => (
            <button
              key={m.id}
              onClick={() => setModel(m)}
              className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition ${
                activeModel?.id === m.id ? 'bg-fuchsia-600 text-white border-fuchsia-600' : 'bg-gray-50 text-gray-500 border-gray-200 hover:border-fuchsia-300'
              }`}
              title={`${m.description} · ${m.credits} crédit(s)`}
            >
              {m.label} · {m.credits}cr
            </button>
          ))}
        </div>
      )}

      {/* Prompt + format + générer */}
      <textarea
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        rows={2}
        className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:border-[#221C51] resize-none"
      />
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {['9:16', '1:1', '16:9', '4:5'].map((r) => (
          <button key={r} onClick={() => setAspect(r)}
            className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${aspect === r ? 'bg-[#221C51] text-white border-[#221C51]' : 'border-gray-200 text-gray-500'}`}>
            {r}
          </button>
        ))}
        <button
          onClick={() => generate()}
          disabled={loading || !activeModel}
          className="ml-auto px-5 py-2 bg-fuchsia-600 text-white text-xs font-semibold rounded-xl hover:bg-fuchsia-700 disabled:opacity-50 transition"
        >
          {loading ? 'Génération…' : `✨ Générer ${isVideoModel ? 'la vidéo' : 'les visuels'}`}
        </button>
      </div>

      {/* Résultats + actions par média */}
      {loading && results.length === 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {[0, 1].map((i) => <div key={i} className="aspect-square rounded-xl bg-gray-100 animate-pulse" />)}
        </div>
      )}
      {results.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          {results.map((r, i) => (
            <div key={`${r.url}-${i}`} className="rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
              {r.type === 'video' ? (
                <video src={r.url} className="w-full aspect-square object-cover" muted playsInline controls />
              ) : (
                <img src={r.url} alt={`Créa ${i + 1}`} className="w-full aspect-square object-cover" />
              )}
              <div className="p-1.5 flex flex-col gap-1">
                <button
                  onClick={() => attach(r)}
                  disabled={busyUrl === r.url}
                  className="w-full text-[10px] font-bold text-white bg-emerald-600 rounded-lg py-1.5 hover:bg-emerald-700 disabled:opacity-50"
                >
                  ✅ Utiliser pour ma pub
                </button>
                <div className="flex gap-1">
                  <button
                    onClick={() => generate()}
                    disabled={loading}
                    title="Nouvelle variation avec le même prompt"
                    className="flex-1 text-[10px] font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg py-1 hover:border-[#221C51]/40 disabled:opacity-50"
                  >
                    🔁 Recréer
                  </button>
                  <button
                    onClick={() => { setEditTarget({ url: r.url, mode: 'improve' }); setEditPrompt(''); }}
                    title="Ajouter une instruction et régénérer"
                    className="flex-1 text-[10px] font-semibold text-gray-600 bg-white border border-gray-200 rounded-lg py-1 hover:border-[#221C51]/40"
                  >
                    ✨ Améliorer
                  </button>
                  {r.type !== 'video' && (
                    <button
                      onClick={() => { setEditTarget({ url: r.url, mode: 'veo' }); setEditPrompt(''); }}
                      title="Transformer cette image en vidéo avec une instruction (Google Veo)"
                      className="flex-1 text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg py-1 hover:bg-indigo-100"
                    >
                      🎞️ Veo
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barre d'instruction (Améliorer / Édition Veo) */}
      {editTarget && (
        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-center gap-2 flex-wrap">
          <span className="text-xs font-bold text-indigo-800 flex-shrink-0">
            {editTarget.mode === 'veo' ? '🎞️ Édition Veo (Google) :' : '✨ Améliorer :'}
          </span>
          <input
            value={editPrompt}
            onChange={(e) => setEditPrompt(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submitEdit()}
            autoFocus
            placeholder={editTarget.mode === 'veo' ? 'Ex : zoom lent sur le produit, lumière dorée…' : 'Ex : fond plus sombre, ajoute le logo…'}
            className="flex-1 min-w-[180px] px-3 py-2 text-xs border border-indigo-300 rounded-lg focus:outline-none focus:border-indigo-500"
          />
          <button onClick={submitEdit} disabled={!editPrompt.trim() || loading}
            className="px-4 py-2 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            Lancer
          </button>
          <button onClick={() => setEditTarget(null)} className="text-xs text-gray-400 hover:text-gray-600 px-1">Annuler</button>
        </div>
      )}
    </div>
  );
};

export default InlineCreativeStudio;
